// React
import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { useFormikContext } from 'formik'

// Components
import InputField from 'ui/components/InputField'
import Day from './Day'
import BPicker from 'ui/blocks/Picker'

// Shared
import moment from 'shared/lib/moment'
import { useTranslation } from 'shared/utils/useTranslation'
import { usePicker } from 'ui/components/Picker/hooks'

const DateTimePicker = ({ field = {}, ...props }) => {
  const { t } = useTranslation('user')
  const form = useFormikContext()

  const picker = usePicker()

  const timeAvailability = useMemo(() => {
    const date = field.value ? moment(field.value) : null

    if (!date || !props.availabilities) return []

    return props.availabilities.filter((el) => moment(el.date).isSame(date, 'day'))
  }, [field.value, props.availabilities])

  const date = field.value ? moment(field.value) : null
  const time = date ? date.format('HH:mm') : null

  const hasDateError = (!!form.errors[field.name]?.date || (!picker.useTimes && !!form.errors[field.name]?.time))
  const hasTimeError = !!form.errors[field.name]?.time

  const handleDateChange = (value, dayClick) => {
    const date = moment(field.value)
    const newDate = moment(value).set({ h: date.hours(), m: date.minutes() })

    field.onChange?.(newDate.toJSON())

    if (!dayClick) return

    picker.onChange(props.mode)
  }

  const handleDateBlur = (e) => {
    props.onBlur?.(e.target.value || field.value)
  }

  const handleTimeChange = (time) => {
    const date = moment(field.value)
    const [hour, minute] = time.split(':')

    field.onChange?.(date.set({ hour, minute, second: 0, millisecond: 0 }).toJSON())

    picker.onChange(`${props.mode}_time`)

    props.onTimeChange?.()
  }

  const handleScrollTimeIntoView = () => {
    const tippy = props.inputTimeRef.current?._tippy

    const popover =
      tippy?.popper.querySelector('[data-tid="Time popover"]') ||
      document.querySelector(`#tippy-${tippy?.id} [data-tid="Time popover"]`)

    if (!popover) return

    if (time) {
      const option = popover.querySelector(`[data-value="${time}"]`)

      // Scroll to time option element if found
      if (option) {
        const containerRect = popover.getBoundingClientRect()
        const optionRect = option.getBoundingClientRect()

        const amPmOffset = props.useAMOrPM ? 45 : 0

        const scrollTop =
          optionRect.top -
          containerRect.top +
          popover.scrollTop -
          (containerRect.height - optionRect.height) / 2 -
          amPmOffset

        popover.scrollTo({
          left: 0,
          top: Math.max(0, scrollTop)
        })
      }
    } else {
      // Scroll to the middle of the popover
      popover.scrollTo({
        left: 0,
        top: Math.max(0, popover.scrollHeight / 2 - popover.clientHeight / 2)
      })
    }
  }

  const handleTimeOpen = () => {
    props.onOpen?.(date)

    // Timeout here to allow the popover to render and be attached to the DOM
    setTimeout(handleScrollTimeIntoView, 1)
  }

  const renderDay = useCallback(
    (day, modifiers) => {
      let available

      if (props.showAvailability && props.lineCount) {
        available = props.availabilities?.getFirst?.({ type: 'date', date: day.toISOString().slice(0, 10) })?.status
      }

      return (
        <Day
          isMobile={props.isMobile}
          showPast={picker.allowPast}
          day={day}
          available={available}
          showAvailability={props.showAvailability && !!props.lineCount}
          modifiers={modifiers}
        />
      )
    },
    [props.showAvailability, props.lineCount, props.availabilityLoading, picker.allowPast, props.availabilities, props.isMobile]
  )

  return (
    <BPicker.PickerWrapper modifiers="no-padding-top">
      <InputField
        {...props}
        field={{
          name: 'date',
          value: date,
          onChange: handleDateChange,
          onBlur: handleDateBlur
        }}
        type="dateButton"
        innerRef={(ref) => {
          props.inputDateRef.current = ref
        }}
        disabled={props.disabledDate}
        error={hasDateError}
        showIcon={false}
        data-value={date?.format('DD-MM-YYYY')}
        placeholder={
          props.translate ? t('user.store.select_date_placeholder') : t('common.order_picker.date_placeholder')
        }
        paddingHorizontal={!props.inline}
        renderDay={renderDay}
        onOpen={props.onOpen?.bind(null, false)}
        mode={props.mode}
        usePortal={props.usePortal?.date ?? props.usePortal}
        forceUTC
        hideErrorLabel
      />
      {picker.useTimes && (
        <InputField
          {...props}
          field={{
            name: 'time',
            value: time,
            onChange: handleTimeChange
          }}
          type="timeButton"
          disabled={props.disabledTime || !date}
          innerRef={(ref) => {
            props.inputTimeRef.current = ref
          }}
          error={hasTimeError}
          data-tid={`${props['data-tid']} time`}
          data-value={time}
          availabilities={timeAvailability}
          placeholder={
            props.translate ? t('user.store.select_time_placeholder') : t('common.order_picker.time_placeholder')
          }
          paddingHorizontal={!props.inline}
          // Fetch times on focus
          onOpen={handleTimeOpen}
          mode={`${props.mode}_time`}
          usePortal={props.usePortal?.time ?? props.usePortal}
          hideErrorLabel
        />
      )}
    </BPicker.PickerWrapper>
  )
}

DateTimePicker.propTypes = {
  'inline': PropTypes.bool,
  'label': PropTypes.any,
  'format': PropTypes.string,
  'usePortal': PropTypes.oneOfType([
    PropTypes.shape({
      date: PropTypes.bool,
      time: PropTypes.bool
    }),
    PropTypes.bool
  ]),
  'useAMOrPM': PropTypes.bool,
  'disabledDate': PropTypes.bool,
  'disabledTime': PropTypes.bool,
  'inputDateRef': PropTypes.any.isRequired,
  'inputTimeRef': PropTypes.any,
  'error': PropTypes.object,
  'availabilities': PropTypes.object,
  'showAvailability': PropTypes.oneOf([true, false, 'count']),
  'availabilityLoading': PropTypes.bool,
  'translate': PropTypes.bool,
  'lineCount': PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  'onOpen': PropTypes.func,
  'onBlur': PropTypes.func,
  'onTimeChange': PropTypes.func,
  'data-tid': PropTypes.string,
  'mode': PropTypes.oneOf(['from', 'to']),
  'isMobile': PropTypes.bool,
  // Form
  'field': PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onInvalid: PropTypes.func,
    /** Custom validation function should return invalid key or true
     * ex. validate = (date) => 'HH'
     */
    validate: PropTypes.func
  })
}

export default DateTimePicker
